// ===== Cliente Page (perfil full-page com blocos modulares) =====
import React from 'react';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { ClienteAvatar, StatusCarteira, TipoChip, whatsappLink } from './clientes.jsx';
import { Select } from './modals.jsx';
import { TabelaDeLinhas, useLinhasDaTabela } from './lista-de-precos.jsx';
import { rotuloDaTabela } from './preco-tabela.js';
import { dataBr } from './data-br.js';
import { usePermission } from './auth.jsx';
import {
  NICHOS_BY_ID, SAC_TIPOS_BY_ID, STAGE_NAMES, TIPO_CLIENTE_BY_ID,
  brl, brlCompact, fiscalOk, recomendacaoParaCliente, sacFmtHoras, sacSla,
} from './data.jsx';
import { estaEmDesenvolvimento } from './tela-em-desenvolvimento.jsx';

const { useState: useStateCP, useMemo: useMemoCP } = React;

function CollapsibleCard({ id, title, icon, defaultOpen = true, badge, action, children }) {
  const [open, setOpen] = useStateCP(defaultOpen);
  return (
    <div className={`cp-card ${open ? "" : "closed"}`} data-block={id}>
      <div className="cp-card-h">
        <button className="cp-h-toggle" onClick={() => setOpen(o => !o)}>
          <Icon name={open ? "chevron-down" : "chevron-right"} size={13} />
        </button>
        <span className="cp-h-ic"><Icon name={icon} size={13} /></span>
        <div className="cp-h-title">{title}</div>
        {badge != null && <span className="cp-h-badge">{badge}</span>}
        <div className="cp-h-actions">{action}</div>
      </div>
      {open && <div className="cp-card-body">{children}</div>}
    </div>
  );
}

// Componente proprio, e nao trecho do ClientePage: a contagem precisa de um
// hook (a tabela manual carrega os itens), e o ClientePage retorna cedo quando
// o cliente nao existe -- hook depois desse retorno seria condicional.
function CartaoTabelaDoCliente({ tabela }) {
  const linhas = useLinhasDaTabela(tabela);
  const n = linhas.length;
  return (
    <CollapsibleCard id="tabela" title="Tabela de preços associada" icon="file-spreadsheet"
      badge={tabela ? `${n.toLocaleString("pt-BR")} ${n === 1 ? "item" : "itens"}` : null}>
      {tabela ? (
        <>
          <div className="tabela-head" style={{ borderColor: tabela.cor, marginBottom: 8 }}>
            <div className="th-icon" style={{ background: tabela.cor }}><Icon name="file-spreadsheet" size={15} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="th-nm">{tabela.nome}</div>
              <div className="th-meta">{tabela.descricao}</div>
            </div>
            <div className="th-desc mono">{rotuloDaTabela(tabela)}</div>
          </div>
          {n === 0
            ? <div className="empty" style={{ padding: 20 }}>Esta tabela ainda não tem produto com preço.</div>
            : <TabelaDeLinhas linhas={linhas} limite={5} dica="" />}
        </>
      ) : <div className="empty" style={{ padding: 20 }}>Nenhuma tabela associada.</div>}
    </CollapsibleCard>
  );
}

function CanalVendasCard({ cliente }) {
  const store = useStore();
  // Decisao do Kaio (30/09): "o financeiro define o repasse" (financeiro,
  // adm_financeiro e admin). O banco confere (0085); a tela so oferece o campo a
  // quem pode, para ninguem digitar o que o banco vai recusar.
  const podeDefinirRepasse = usePermission('clientes.definir_repasse');
  const esp = store.getEspecificadorById(cliente.id) || {};
  const [editing, setEditing] = useStateCP(false);
  const [form, setForm] = useStateCP({
    canalTipo: cliente.canalTipo || esp.tipo || "Arquitetura",
    canalResponsavel: cliente.canalResponsavel || esp.responsavel || cliente.contato || "",
    parceriaRepasse: cliente.parceriaRepasse ?? esp.parceriaRepasse ?? false,
    pctRepasse: cliente.pctRepasse ?? esp.pctRepasse ?? 0,
    ativo: esp.ativo ?? true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    const patch = { canalTipo: form.canalTipo, canalResponsavel: form.canalResponsavel, parceriaRepasse: form.parceriaRepasse, canalAtivo: form.ativo };
    // A % so viaja se quem define a ALTEROU: o salvamento do canal nao pode depender
    // de a pessoa poder mudar a %, e a % e dinheiro (0 a 100).
    const nova = Number(form.pctRepasse);
    const atual = Number(cliente.pctRepasse ?? esp.pctRepasse ?? 0);
    if (podeDefinirRepasse && nova !== atual) {
      if (!Number.isFinite(nova) || nova < 0 || nova > 100) {
        store.toast({ kind: "err", msg: "A % do repasse tem de ficar entre 0 e 100." });
        return;
      }
      patch.pctRepasse = nova;
    }
    // "Atualizado" so DEPOIS de o banco aceitar: antes o aviso vinha na hora e o erro,
    // se houvesse, chegava depois -- a pessoa via os dois.
    const salvou = await store.updateCliente(cliente.id, patch);
    if (salvou) store.toast({ kind: "ok", msg: "Canal de vendas atualizado" });
    setEditing(false);
  };

  return (
    <div className="cp-card canal-card-block" data-block="canal">
      <div className="cp-card-h">
        <span className="cp-h-ic canal"><Icon name="compass" size={13} /></span>
        <div className="cp-h-title">Canal de vendas</div>
        <span className="cp-h-badge">{esp.clientesCount || 0} clientes · {brlCompact(esp.receitaGerada || 0)}</span>
        <div className="cp-h-actions">
          {editing
            ? <><button className="btn ghost sm" onClick={() => setEditing(false)}>Cancelar</button><button className="btn primary sm" onClick={save}><Icon name="save" size={12} /> Salvar</button></>
            : <button className="btn sm" onClick={() => setEditing(true)}><Icon name="pencil" size={12} /> Editar</button>}
        </div>
      </div>
      <div className="cp-card-body">
        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Tipo de canal</label>
                <Select value={form.canalTipo} onChange={v => set("canalTipo", v)} options={["Arquitetura", "Designer", "Construtora", "Marcenaria", "Representante", "Showroom", "Lojista", "Outro"]} />
              </div>
              <div className="field"><label>Responsável</label><div className="control"><input value={form.canalResponsavel} onChange={e => set("canalResponsavel", e.target.value)} /></div></div>
            </div>
            <label className="check-row"><input type="checkbox" checked={form.parceriaRepasse} onChange={e => set("parceriaRepasse", e.target.checked)} /><span>Parceria com repasse de comissão</span></label>
            {/* Editavel so para quem define (clientes.definir_repasse); os demais veem a
                % do banco e quem a define. O campo digitavel para todos derrubava o
                salvamento inteiro do canal (42501) quando a coluna era trancada. */}
            {form.parceriaRepasse && (
              <div className="field" style={{ maxWidth: 260 }}>
                <label>% de repasse</label>
                {podeDefinirRepasse
                  ? <div className="control mono"><input type="number" min="0" max="100" step="0.01" value={form.pctRepasse} onChange={e => set("pctRepasse", e.target.value)} /> %</div>
                  : <div className="control mono" style={{ opacity: 0.75 }}>{form.pctRepasse}%</div>}
                <div style={{ fontSize: 11, color: "var(--color-ink-soft)", marginTop: 4 }}>
                  {podeDefinirRepasse ? "Você define esta %. Vale para os negócios do canal a partir da próxima gravação deles." : "Definida pelo Financeiro."}
                </div>
              </div>
            )}
            <label className="check-row"><input type="checkbox" checked={form.ativo} onChange={e => set("ativo", e.target.checked)} /><span>Canal ativo</span></label>
          </div>
        ) : (
          <div className="canal-view">
            <div className="cv-pair"><span className="k">Tipo</span><span className="v">{form.canalTipo}</span></div>
            <div className="cv-pair"><span className="k">Responsável</span><span className="v">{form.canalResponsavel || "—"}</span></div>
            <div className="cv-pair"><span className="k">Repasse</span><span className="v">{form.parceriaRepasse ? <><span className="mono">{form.pctRepasse}%</span> sobre projetos fechados</> : "Sem repasse"}</span></div>
            <div className="cv-pair"><span className="k">Situação</span><span className="v">{form.ativo ? <span className="doc-valid ok"><Icon name="check" size={10} /> ativo</span> : <span className="doc-valid err">inativo</span>}</span></div>
            <div className="reco-note" style={{ marginTop: 4 }}><Icon name="info" size={11} /> Este cliente aparece como canal selecionável ao criar negócios e também pode fazer pedidos próprios.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Bloco SAC na ficha do cliente ───────────────────────────────────────────
function ClienteSacCard({ cliente, onOpenChamado, onNewChamado }) {
  const store = useStore();
  const chamados = store.getChamadosByCliente(cliente.id);
  const abertos = chamados.filter(c => c.status !== "resolvido" && c.status !== "fechado");
  const fechados = chamados.filter(c => c.status === "resolvido" || c.status === "fechado");
  const comCsat = chamados.filter(c => c.csat != null);
  const csatMedio = comCsat.length ? Math.round(comCsat.reduce((s, c) => s + c.csat, 0) / comCsat.length * 10) / 10 : null;
  const ultimo = chamados.length ? [...chamados].sort((a, b) => b.abertoEm - a.abertoEm)[0] : null;
  const emRisco = abertos.filter(c => { const s = sacSla(c).status; return s === "risco" || s === "estourado"; }).length;

  const badgeNode = abertos.length > 0
    ? <span style={{ background: emRisco > 0 ? "var(--color-danger-soft)" : "var(--color-surface-2)", color: emRisco > 0 ? "var(--color-danger)" : "var(--color-ink-soft)", fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 700, padding: "1px 8px", borderRadius: 999, border: "1px solid var(--color-border)" }}>
        {abertos.length} aberto{abertos.length > 1 ? "s" : ""}{emRisco > 0 ? ` · ${emRisco} em risco` : ""}
      </span>
    : chamados.length > 0 ? <span className="cp-h-badge">{chamados.length} total</span> : null;

  return (
    <CollapsibleCard id="sac" title="SAC" icon="life-buoy" badge={badgeNode}
      action={estaEmDesenvolvimento('/sac') ? null
        : <button className="btn sm" onClick={() => onNewChamado({ clienteId: cliente.id })}><Icon name="plus" size={12} /> Chamado</button>}>
      {chamados.length === 0 ? (
        <div className="vinc-empty">
          <Icon name="life-buoy" size={16} style={{ color: "var(--color-ink-faint)" }} />
          <span>Nenhum chamado registrado para este cliente.</span>
        </div>
      ) : (
        <div className="cp-sac-body">
          <div className="cp-sac-kpis">
            <div className={`cp-sac-kpi ${emRisco > 0 ? "warn" : ""}`}>
              <div className="lbl">Abertos</div>
              <div className="val mono">{abertos.length}</div>
              <div className="sub">{emRisco > 0 ? `${emRisco} em risco` : "em andamento"}</div>
            </div>
            <div className={`cp-sac-kpi ${csatMedio === null ? "" : csatMedio >= 4 ? "ok" : "warn"}`}>
              <div className="lbl">CSAT médio</div>
              <div className="val mono">{csatMedio !== null ? csatMedio : "—"}<span style={{ fontSize: 11 }}>{csatMedio !== null ? "/5" : ""}</span></div>
              <div className="sub">{comCsat.length} avaliações</div>
            </div>
            <div className="cp-sac-kpi">
              <div className="lbl">Total</div>
              <div className="val mono">{chamados.length}</div>
              <div className="sub">{fechados.length} resolvidos</div>
            </div>
          </div>

          {abertos.length > 0 && (
            <div className="cp-sac-section">
              <div className="cp-sac-sh">Em aberto · {abertos.length}</div>
              {abertos.slice(0, 3).map(c => {
                const tipo = SAC_TIPOS_BY_ID[c.tipo];
                const sla = sacSla(c);
                const slaColor = { ok: "var(--color-ok)", risco: "var(--color-warn)", estourado: "var(--color-danger)", pausado: "var(--color-ink-soft)" }[sla.status] || "var(--color-ink-soft)";
                return (
                  <button key={c.id} className="cp-sac-row" onClick={() => onOpenChamado(c.id)}>
                    <span className={`sac-status-dot st-${c.status}`} />
                    <div className="cp-sac-row-body">
                      <div className="nm">{c.assunto}</div>
                      <div className="meta">
                        <span style={{ background: tipo?.tone, color: tipo?.color, fontSize: 9, padding: "1px 5px", borderRadius: 99, fontWeight: 700 }}>{tipo?.short}</span>
                        <span>· {c.abertoQuando}</span>
                      </div>
                    </div>
                    <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: slaColor, flexShrink: 0 }}>
                      {sla.status === "pausado" ? "⏸" : sla.status === "estourado" ? `+${sacFmtHoras(Math.abs(sla.restanteH))}` : sacFmtHoras(sla.restanteH)}
                    </span>
                    <Icon name="chevron-right" size={12} style={{ color: "var(--color-ink-faint)", flexShrink: 0 }} />
                  </button>
                );
              })}
              {abertos.length > 3 && <div className="cp-sac-more">+{abertos.length - 3} mais</div>}
            </div>
          )}

          {ultimo && (
            <div className="cp-sac-section">
              <div className="cp-sac-sh">Último chamado</div>
              <button className="cp-sac-row" onClick={() => onOpenChamado(ultimo.id)}>
                <span className={`sac-status-dot st-${ultimo.status}`} />
                <div className="cp-sac-row-body">
                  <div className="nm">{ultimo.assunto}</div>
                  <div className="meta">
                    <span style={{ background: SAC_TIPOS_BY_ID[ultimo.tipo]?.tone, color: SAC_TIPOS_BY_ID[ultimo.tipo]?.color, fontSize: 9, padding: "1px 5px", borderRadius: 99, fontWeight: 700 }}>{SAC_TIPOS_BY_ID[ultimo.tipo]?.short}</span>
                    {ultimo.csat != null && <span style={{ color: ultimo.csat >= 4 ? "var(--color-ok)" : "var(--color-warn)", fontWeight: 700 }}>★ {ultimo.csat}/5</span>}
                    <span>· {ultimo.abertoQuando}</span>
                  </div>
                </div>
                <Icon name="chevron-right" size={12} style={{ color: "var(--color-ink-faint)", flexShrink: 0 }} />
              </button>
            </div>
          )}
        </div>
      )}
    </CollapsibleCard>
  );
}

function ClientePage({ clienteId, onOpenLead, onCreateAlert, onBack, onPreviewPdf, onEdit, onOpenChamado, onNewChamado }) {
  const store = useStore();
  const cliente = store.getClienteById(clienteId);
  if (!cliente) return (
    <div className="main">
      <div className="acesso-negado">
        <div className="an-ic"><Icon name="search" size={22} /></div>
        <div className="an-tt">Cliente não encontrado</div>
        <div className="an-sub">Este cadastro não existe mais ou o link está desatualizado.</div>
        <button className="btn primary" style={{ marginTop: 4 }} onClick={onBack}><Icon name="chevron-left" size={13} /> Voltar aos clientes</button>
      </div>
    </div>
  );

  const tipo = TIPO_CLIENTE_BY_ID[cliente.tipo];
  const nicho = cliente.nicho ? NICHOS_BY_ID[cliente.nicho] : null;
  const canal = cliente.canalId ? store.getEspecificadorById(cliente.canalId) : null;
  const negocios = store.getLeadsByCliente(cliente.id);
  const alertasPend = store.alerts.filter(a => a.clienteId === cliente.id || negocios.some(n => n.id === a.leadId)).filter(a => !a.feito).length;
  const wpp = whatsappLink(cliente.telefone);
  const tabela = cliente.tabelaId ? store.getTabelaById(cliente.tabelaId) : null;
  const recomendacoes = recomendacaoParaCliente(cliente);
  const curva = cliente.curvaConsumo || [];
  const maxCurva = curva[0]?.valorTotal || 1;
  const docOk = fiscalOk(cliente);
  const contatos = (cliente.contatos && cliente.contatos.length > 0) ? cliente.contatos : [{ id: "ct_fallback", nome: cliente.contato, cargo: cliente.cargo, telefone: cliente.telefone, email: cliente.email, principal: true }];

  return (
    <div className="main main-scroll cliente-page" data-screen-label={`Cliente / ${cliente.nome}`}>
      {/* Hero header */}
      <div className="cp-hero" style={{ background: `linear-gradient(180deg, ${tipo?.tone || "var(--color-bg)"} 0%, var(--color-bg) 100%)` }}>
        <button className="btn ghost sm" onClick={onBack}><Icon name="chevron-left" size={13} /> Voltar para Clientes</button>
        <div className="cp-hero-main">
          <ClienteAvatar cliente={cliente} size={64} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cp-hero-title">{cliente.nome}</div>
            <div className="cp-hero-meta">
              <TipoChip tipo={cliente.tipo} />
              <StatusCarteira status={cliente.status} reativacao={cliente.reativacao} />
              {cliente.isCanal && <span className="canal-badge lg" title="Também é canal de vendas"><Icon name="compass" size={11} /> Canal de vendas</span>}
              {nicho && <span className="cp-niche-pill"><Icon name={nicho.icon} size={11} /> {nicho.label}</span>}
              <span className="cp-meta-sep">·</span>
              <span><Icon name="globe" size={11} /> {cliente.cidade}</span>
              <span className="cp-meta-sep">·</span>
              <span><Icon name="history" size={11} /> Cliente desde {dataBr(cliente.criadoEm)}</span>
            </div>
          </div>
          <div className="cp-hero-actions">
            {wpp && <a href={wpp} target="_blank" rel="noreferrer" className="btn"><Icon name="message-square" size={13} /> WhatsApp</a>}
            <button className="btn" onClick={onCreateAlert}><Icon name="check-circle-2" size={13} /> Nova tarefa</button>
            <button className="btn primary" onClick={onEdit}><Icon name="pencil" size={13} /> Editar</button>
          </div>
        </div>
        <div className="cp-hero-kpis">
          <div className="cp-kpi"><div className="lbl">Faturamento</div><div className="val mono">{brlCompact(cliente.faturamento)}</div></div>
          <div className="cp-kpi"><div className="lbl">Negócios ativos</div><div className="val mono">{cliente.dealsAtivos}<span className="sub"> / {cliente.dealsTotal} total</span></div></div>
          <div className="cp-kpi"><div className="lbl">Última compra</div><div className="val">{cliente.lastBuy}</div></div>
          <div className="cp-kpi"><div className="lbl">Tarefas pendentes</div><div className="val mono" style={{ color: alertasPend > 0 ? "var(--color-warn)" : "var(--color-ink)" }}>{alertasPend}</div></div>
        </div>
        <div className="cp-hero-hint">
          <Icon name="grip" size={11} />
          <span>Perfil modular · clique em qualquer bloco para colapsar. Arrastar para reordenar em breve.</span>
        </div>
      </div>

      {/* Grid of cards */}
      <div className="cp-grid">
        {/* COLUNA 1 */}
        <div className="cp-col">
          <CollapsibleCard id="dados" title="Contatos" icon="user" badge={contatos.length} action={<button className="btn sm" onClick={onEdit}><Icon name="plus" size={12} /> Contato</button>}>
            <div className="contato-list">
              {contatos.map(ct => {
                const w = whatsappLink(ct.telefone);
                return (
                  <div key={ct.id} className="contato-row">
                    <div className="ct-av">{(ct.nome || "—").split(" ").filter(Boolean).map(p => p[0]).slice(0, 2).join("").toUpperCase()}</div>
                    <div className="ct-body">
                      <div className="nm">{ct.nome}{ct.principal && <span className="ct-main">principal</span>}</div>
                      <div className="meta">{ct.cargo || "—"} · <span className="mono">{ct.telefone || "—"}</span> · {ct.email || "—"}</div>
                    </div>
                    {w && <a href={w} target="_blank" rel="noreferrer" className="wpp-btn" title="WhatsApp Web"><Icon name="message-square" size={13} /></a>}
                  </div>
                );
              })}
            </div>
            {nicho && (
              <div className="field" style={{ marginTop: 10 }}><label>Nicho</label><div className="control"><Icon name={nicho.icon} size={13} style={{ color: "var(--color-accent-deep)", marginRight: 8 }} /><input readOnly value={nicho.label} /></div></div>
            )}
          </CollapsibleCard>

          {cliente.isCanal && <CanalVendasCard cliente={cliente} />}

          <CollapsibleCard id="fiscal" title="Cadastro fiscal e faturamento" icon="shield-check" badge={docOk ? <span className="doc-valid ok"><Icon name="check" size={10} /> válido</span> : <span className="doc-valid err"><Icon name="alert-triangle" size={10} /> verificar</span>}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="field"><label>Tipo</label><div className="control"><input readOnly value={cliente.tipoDoc === "cnpj" ? "Pessoa Jurídica" : "Pessoa Física"} /></div></div>
              <div className="field"><label>{cliente.tipoDoc === "cnpj" ? "CNPJ" : "CPF"}</label><div className="control mono"><input readOnly value={cliente.tipoDoc === "cnpj" ? cliente.cnpj : cliente.cpf} /></div></div>
              {cliente.tipoDoc === "cnpj" && <div className="field"><label>Inscrição Estadual</label><div className="control mono"><input readOnly value={cliente.ie || "isento"} /></div></div>}
            </div>
            {!docOk && (
              <div className="fiscal-warn">
                <Icon name="alert-triangle" size={13} />
                <span>Cadastro fiscal incompleto — <strong>obrigatório para gerar propostas</strong>.</span>
                <button className="btn sm" onClick={onEdit}>Completar</button>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <div className="field"><label>E-mail de faturamento · NF-e</label><div className="control"><Icon name="mail" size={13} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} /><input readOnly value={cliente.emailFinanceiro || cliente.email || "—"} /></div></div>
              <div className="field"><label>Condição de pagamento</label><div className="control mono"><input readOnly value={cliente.condPagamento || "—"} /></div></div>
            </div>
          </CollapsibleCard>

          <CollapsibleCard id="enderecos" title="Endereços" icon="globe">
            <div className="addr-block">
              <div className="addr-h">Faturamento</div>
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10 }}>
                <div className="field"><label>CEP</label><div className="control mono"><input readOnly value={cliente.endereco.cep} /></div></div>
                <div className="field"><label>Bairro</label><div className="control"><input readOnly value={cliente.endereco.bairro} /></div></div>
              </div>
              <div className="field"><label>Logradouro</label><div className="control"><input readOnly value={`${cliente.endereco.rua}, ${cliente.endereco.num}${cliente.endereco.complemento ? " · " + cliente.endereco.complemento : ""}`} /></div></div>
              <div className="field"><label>Cidade / UF</label><div className="control"><input readOnly value={`${cliente.endereco.cidade} · ${cliente.endereco.uf}`} /></div></div>
            </div>
            <div className="addr-block">
              <div className="addr-h">Entrega {cliente.enderecoEntregaMesmo ? <span className="addr-same">mesmo do faturamento</span> : ""}</div>
              {!cliente.enderecoEntregaMesmo && cliente.enderecoEntrega && (
                <div className="field"><label>Endereço</label><div className="control"><input readOnly value={`${cliente.enderecoEntrega.rua}, ${cliente.enderecoEntrega.num} · ${cliente.enderecoEntrega.bairro} · ${cliente.enderecoEntrega.cidade}/${cliente.enderecoEntrega.uf}`} /></div></div>
              )}
            </div>
          </CollapsibleCard>

          <CollapsibleCard id="curva" title="Curva de consumo" icon="package" badge={curva.length > 0 ? `${curva.length} produtos` : "—"}>
            {curva.length === 0 ? (
              <div className="empty" style={{ padding: 20 }}>Sem histórico ainda.</div>
            ) : (
              <div className="curva-list">
                {curva.map((c, i) => (
                  <div key={i} className="curva-row">
                    <div className="cv-rank mono">{i + 1}</div>
                    <div className="cv-info">
                      <div className="nm">{c.nome}</div>
                      <div className="meta">{c.categoria} · {c.qtdTotal} un. · última compra {c.ultimaCompra}</div>
                    </div>
                    <div className="cv-bar"><div className="fill" style={{ width: Math.round((c.valorTotal / maxCurva) * 100) + "%" }} /></div>
                    <div className="cv-val mono">{brlCompact(c.valorTotal)}</div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        </div>

        {/* COLUNA 2 */}
        <div className="cp-col">
          <CollapsibleCard id="canal" title="Canal / Especificador" icon="compass">
            {canal ? (
              <div className="canal-card">
                <div className="canal-card-ic"><Icon name="compass" size={16} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{canal.nome}</div>
                  <div className="meta">{canal.tipo} · {canal.cidade} · responsável: {canal.responsavel}</div>
                </div>
                <div className="canal-card-tag mono">{canal.pctRepasse}% repasse</div>
              </div>
            ) : (
              <div className="vinc-empty">
                <Icon name="compass" size={16} style={{ color: "var(--color-ink-faint)" }} />
                <span>Cliente direto · sem canal intermediário.</span>
              </div>
            )}
          </CollapsibleCard>

          <CartaoTabelaDoCliente tabela={tabela} />

          <CollapsibleCard id="recomendacoes" title="Recomendações por nicho" icon="sparkles" badge={recomendacoes.length}>
            {nicho ? (
              <>
                <div className="reco-grid">
                  {recomendacoes.map(p => (
                    <div key={p.id} className="reco-card">
                      <div className="rc-ic"><Icon name="sparkles" size={13} /></div>
                      <div className="rc-body">
                        <div className="nm">{p.nome}</div>
                        <div className="meta">{p.categoria} · giro {p.giro}</div>
                      </div>
                      <div className="rc-price mono">{brl(p.precoLista)}</div>
                    </div>
                  ))}
                  {recomendacoes.length === 0 && <div className="empty" style={{ gridColumn: "1/-1", padding: 14 }}>Cliente já consome todos os produtos sugeridos para o nicho.</div>}
                </div>
                <div className="reco-note">
                  <Icon name="info" size={11} />
                  Motor baseado no nicho <strong>{nicho.label}</strong>. Foco: {nicho.focus}.
                </div>
              </>
            ) : <div className="empty" style={{ padding: 20 }}>Cadastre o nicho para receber sugestões.</div>}
          </CollapsibleCard>

          {onOpenChamado && onNewChamado && (
            <ClienteSacCard cliente={cliente} onOpenChamado={onOpenChamado} onNewChamado={onNewChamado} />
          )}

          <CollapsibleCard id="negocios" title="Negócios vinculados" icon="layout-kanban" badge={`${negocios.filter(n => n.stage !== "GANHO" && !n.perdido).length} ativos · ${negocios.length} total`}>
            {negocios.length === 0 ? (
              <div className="empty" style={{ padding: 20 }}>Sem negócios.</div>
            ) : (
              <div>
                {[
                  { label: "Em andamento", tone: "accent", items: negocios.filter(n => n.stage !== "GANHO" && !n.perdido) },
                  { label: "Ganhos",       tone: "ok",     items: negocios.filter(n => n.stage === "GANHO" && !n.perdido) },
                  { label: "Perdidos",     tone: "danger", items: negocios.filter(n => n.perdido) },
                ].map(g => g.items.length === 0 ? null : (
                  <div key={g.label} className="neg-group">
                    <div className={`neg-group-h tone-${g.tone}`}>{g.label} · <span className="mono">{g.items.length}</span></div>
                    {g.items.map(n => (
                      <button key={n.id} className="neg-row" onClick={() => onOpenLead(n.id)}>
                        <span className={`stage-dot stage-${n.stage}`} />
                        <div className="neg-row-body">
                          <div className="nm">{n.empresa}</div>
                          <div className="meta">{STAGE_NAMES[n.stage]} · {n.proximaAcao}</div>
                        </div>
                        <div className="neg-val mono">{n.valor > 0 ? brl(n.valor) : "—"}</div>
                        {n.motivoPerda && <span className="lost-tag">{n.motivoPerda}</span>}
                        <Icon name="chevron-right" size={13} style={{ color: "var(--color-ink-faint)" }} />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        </div>
      </div>
    </div>
  );
}

export { ClientePage, CollapsibleCard };
